import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import {
  facebookLeadFormsService,
  STANDARD_LEAD_QUESTIONS,
  type CreateLeadFormPayload,
  type LeadGenFormQuestion,
} from '@/services/facebook-lead-forms.service';
import { Check, Circle, ChevronRight, ClipboardList, Settings, MessageSquare, Plus, X, GripVertical, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const LOCALES = [
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'ar_AR', label: 'Arabic' },
  { code: 'es_ES', label: 'Spanish' },
  { code: 'fr_FR', label: 'French' },
  { code: 'de_DE', label: 'German' },
  { code: 'hi_IN', label: 'Hindi' },
  { code: 'ur_PK', label: 'Urdu' },
];

const CTA_TYPES = [
  { value: 'VIEW_WEBSITE', label: 'View website' },
  { value: 'CALL_BUSINESS', label: 'Call business' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'NONE', label: 'No button' },
] as const;

const FORM_TYPES = [
  { value: 'MORE_VOLUME', label: 'More volume (default)', hint: 'Easier path — fewer steps for the user' },
  { value: 'HIGHER_INTENT', label: 'Higher intent (review screen)', hint: 'Adds a review step. Requires Lead Ads capability from Meta App Review' },
] as const;

const STEPS = [
  { label: 'Basics', icon: <ClipboardList size={15} /> },
  { label: 'Questions', icon: <Plus size={15} /> },
  { label: 'Thank-you', icon: <MessageSquare size={15} /> },
  { label: 'Settings', icon: <Settings size={15} /> },
];

export default function LeadFormBuilder() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  // Step 1 — Basics
  const [name, setName] = useState('');
  const [locale, setLocale] = useState('en_US');
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');

  // Step 2 — Questions
  const [questions, setQuestions] = useState<LeadGenFormQuestion[]>([
    { type: 'EMAIL' },
    { type: 'FULL_NAME' },
  ]);

  // Step 3 — Thank-you screen
  const [thankYouBody, setThankYouBody] = useState("Thanks! We'll be in touch shortly.");
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [ctaType, setCtaType] = useState<CreateLeadFormPayload['ctaType']>('VIEW_WEBSITE');
  const [ctaText, setCtaText] = useState('Visit our site');

  // Step 4 — Settings
  const [formType, setFormType] = useState<CreateLeadFormPayload['formType']>('MORE_VOLUME');
  const [isOptimizedForQuality, setIsOptimizedForQuality] = useState(false);

  const addStandard = (type: string) => {
    if (questions.some(q => q.type === type)) return;
    setQuestions(prev => [...prev, { type }]);
  };

  const addCustom = () => {
    setQuestions(prev => [...prev, { type: 'CUSTOM', label: 'My question', key: `custom_${prev.length}` }]);
  };

  const removeQuestion = (i: number) => {
    setQuestions(prev => prev.filter((_, idx) => idx !== i));
  };

  const moveQuestion = (i: number, dir: -1 | 1) => {
    setQuestions(prev => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const updateCustomLabel = (i: number, label: string) => {
    setQuestions(prev => prev.map((q, idx) => idx === i ? { ...q, label } : q));
  };

  const stepValid = (s: number) => {
    if (s === 0) {
      return !!name && /^https?:\/\/.+/.test(privacyPolicyUrl);
    }
    if (s === 1) return questions.length > 0;
    if (s === 2) {
      if (ctaType === 'VIEW_WEBSITE') return !!websiteUrl && /^https?:\/\/.+/.test(websiteUrl);
      return true;
    }
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload: CreateLeadFormPayload = {
        name,
        privacyPolicyUrl,
        locale,
        questions,
        thankYouBody,
        ctaType,
      };
      if (websiteUrl) payload.websiteUrl = websiteUrl;
      if (ctaText && ctaType === 'VIEW_WEBSITE') payload.ctaText = ctaText;
      if (formType && formType !== 'MORE_VOLUME') payload.formType = formType;
      if (isOptimizedForQuality) payload.isOptimizedForQuality = true;

      const r = await facebookLeadFormsService.create(payload);
      toast.success(`Form "${r.name}" created`);
      nav(`/facebook/lead-forms/${r.id}`);
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Topbar
        title="New Lead Form"
        breadcrumb="Facebook Ads / Lead Forms / New"
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => nav('/facebook/lead-forms')}>Cancel</Button>
            {step < STEPS.length - 1 ? (
              <Button disabled={!stepValid(step)} onClick={() => setStep(s => s + 1)} iconRight={<ChevronRight size={14} />}>
                Next
              </Button>
            ) : (
              <Button loading={busy} onClick={submit}>Create form</Button>
            )}
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[800px] px-7 py-7 space-y-5">
        {/* Stepper */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s.label} className="flex items-center gap-2">
              <button
                onClick={() => { if (i < step || stepValid(i)) setStep(i); }}
                className={clsx(
                  'flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors',
                  i < step
                    ? 'bg-gold-gradient text-on-primary shadow-soft'
                    : i === step
                    ? 'border border-primary bg-primary-soft text-primary'
                    : 'border border-border text-text-muted',
                )}
              >
                {i < step ? <Check size={13} /> : i === step ? s.icon : <Circle size={13} />}
                {s.label}
              </button>
              {i < STEPS.length - 1 && (
                <div className={clsx('h-px w-6 transition-colors', i < step ? 'bg-primary' : 'bg-border')} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1 — Basics */}
        {step === 0 && (
          <Card>
            <SectionHeader title="Form basics" icon={<ClipboardList size={16} />} />
            <FormField label="Form name" required hint="Internal name; users won't see this">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Q1 Leads Campaign" />
            </FormField>
            <FormField label="Form language" required>
              <Select value={locale} onChange={(e) => setLocale(e.target.value)}>
                {LOCALES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </Select>
            </FormField>
            <FormField
              label="Privacy policy URL"
              required
              hint="Required by Meta. Must be a public HTTPS page describing how you handle leads."
            >
              <Input
                type="url"
                value={privacyPolicyUrl}
                onChange={(e) => setPrivacyPolicyUrl(e.target.value)}
                placeholder="https://slightercrm.com/privacy"
              />
            </FormField>
          </Card>
        )}

        {/* Step 2 — Questions */}
        {step === 1 && (
          <Card>
            <SectionHeader title="Questions" icon={<Plus size={16} />} subtitle={`${questions.length} question${questions.length === 1 ? '' : 's'}`} />
            <div className="space-y-2 mb-4">
              {questions.map((q, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-border bg-surface p-2.5">
                  <button onClick={() => moveQuestion(i, -1)} disabled={i === 0} className="text-text-muted hover:text-text-primary disabled:opacity-30">
                    <GripVertical size={14} />
                  </button>
                  <div className="flex-1 min-w-0">
                    {q.type === 'CUSTOM' ? (
                      <Input
                        value={q.label ?? ''}
                        onChange={(e) => updateCustomLabel(i, e.target.value)}
                        placeholder="Your question"
                        className="text-sm"
                      />
                    ) : (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-semibold text-text-primary">{STANDARD_LEAD_QUESTIONS.find(s => s.type === q.type)?.label ?? q.type}</span>
                        <span className="font-mono text-2xs text-text-muted">{q.type}</span>
                      </div>
                    )}
                  </div>
                  <button onClick={() => removeQuestion(i)} className="text-text-muted hover:text-danger">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold text-text-muted">Add a question</p>
              <div className="flex flex-wrap gap-1.5">
                {STANDARD_LEAD_QUESTIONS.map(s => (
                  <button
                    key={s.type}
                    onClick={() => addStandard(s.type)}
                    disabled={questions.some(q => q.type === s.type)}
                    className="rounded-full border border-border bg-surface px-3 py-1 text-xs text-text-secondary hover:bg-surface-alt hover:border-border-strong disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    + {s.label}
                  </button>
                ))}
                <button
                  onClick={addCustom}
                  className="rounded-full border border-primary bg-primary-soft px-3 py-1 text-xs font-semibold text-primary hover:bg-primary-soft transition-colors"
                >
                  + Custom question
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3 — Thank-you screen */}
        {step === 2 && (
          <Card>
            <SectionHeader title="Thank-you screen" icon={<MessageSquare size={16} />} />
            <FormField label="Message" hint="≤ 500 chars. Plain text only.">
              <textarea
                value={thankYouBody}
                onChange={(e) => setThankYouBody(e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary focus:outline-none"
              />
            </FormField>
            <FormField label="Call-to-action button">
              <Select value={ctaType ?? 'NONE'} onChange={(e) => setCtaType(e.target.value as CreateLeadFormPayload['ctaType'])}>
                {CTA_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </Select>
            </FormField>
            {ctaType === 'VIEW_WEBSITE' && (
              <>
                <FormField label="Website URL" required>
                  <Input
                    type="url"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    placeholder="https://slightercrm.com/thank-you"
                  />
                </FormField>
                <FormField label="Button text">
                  <Input
                    value={ctaText}
                    onChange={(e) => setCtaText(e.target.value)}
                    placeholder="Visit our site"
                    maxLength={60}
                  />
                </FormField>
              </>
            )}
          </Card>
        )}

        {/* Step 4 — Settings */}
        {step === 3 && (
          <Card>
            <SectionHeader title="Form settings" icon={<Settings size={16} />} />
            <FormField label="Form type" hint="Controls how easy it is for the user to submit">
              <Select value={formType ?? 'MORE_VOLUME'} onChange={(e) => setFormType(e.target.value as CreateLeadFormPayload['formType'])}>
                {FORM_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </Select>
              <p className="mt-1 text-2xs text-text-muted">{FORM_TYPES.find(f => f.value === formType)?.hint}</p>
            </FormField>
            {formType === 'HIGHER_INTENT' && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <div>
                  <strong>HIGHER_INTENT requires Meta App approval.</strong> Submit the "Lead Ads — Higher Intent"
                  capability in App Review → Capabilities. Form will fail with `(#3) Application does not have the
                  capability` until approved.
                </div>
              </div>
            )}
            <label className="mt-2 flex items-start gap-2 text-sm text-text-primary cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary-soft"
                checked={isOptimizedForQuality}
                onChange={(e) => setIsOptimizedForQuality(e.target.checked)}
              />
              <div>
                <span className="font-semibold">Optimize for quality leads</span>
                <p className="mt-0.5 text-2xs text-text-muted">Meta reviews the form more strictly + nudges users away from accidental taps. Usually lower volume, higher conversion downstream.</p>
              </div>
            </label>
          </Card>
        )}

        {/* Bottom nav */}
        <div className="flex justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(s => s - 1)}>
            Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              disabled={!stepValid(step)}
              onClick={() => setStep(s => s + 1)}
              iconRight={<ChevronRight size={14} />}
            >
              Next: {STEPS[step + 1].label}
            </Button>
          ) : (
            <Button loading={busy} onClick={submit}>Create form</Button>
          )}
        </div>
      </div>
    </>
  );
}
